/**
 * Console toast store — a tiny, self-contained notification queue.
 *
 * Why NOT the repo-wide Sonner toaster (``@ando/ui`` ``useToast``): Sonner
 * mounts ONCE at the app root, OUTSIDE the console theme scope, so its
 * surfaces can't reach the ``--con-*`` variables (which live under
 * ``.console-theme``) and would paint with the light-only brand tokens. The
 * console needs a toaster that renders inside its own theme scope (see
 * ``ConToaster``, which stamps ``useConsoleThemeClass()`` on the portal).
 *
 * The store is a plain zustand singleton (same primitive the console rail
 * uses): feature code fires ``consoleToast.success(...)`` from any mutation
 * callback, and the mounted ``<ConToaster />`` subscribes and renders. Each
 * toast auto-dismisses after ``duration`` ms (owned by the renderer).
 */

import { create } from "zustand";

export type ConToastTone = "success" | "danger" | "info";

export interface ConToast {
  readonly id: string;
  readonly tone: ConToastTone;
  /** One-line headline (already localized by the caller). */
  readonly title: string;
  /** Optional second line (already localized). */
  readonly description?: string;
  /** Auto-dismiss delay in ms; ``0`` keeps it until dismissed by hand. */
  readonly duration: number;
}

/** What a caller passes — id + duration are defaulted by the store. */
export interface ConToastInput {
  tone: ConToastTone;
  title: string;
  description?: string;
  duration?: number;
}

interface ConToastState {
  toasts: ConToast[];
  push: (input: ConToastInput) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

/** Default lifespan — long enough to read a two-line message, comfortably. */
const DEFAULT_DURATION = 4200;

function newId(): string {
  const c = typeof crypto !== "undefined" ? crypto : undefined;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export const useConsoleToastStore = create<ConToastState>((set) => ({
  toasts: [],
  push: (input) => {
    const id = newId();
    const toast: ConToast = {
      id,
      tone: input.tone,
      title: input.title,
      description: input.description,
      duration: input.duration ?? DEFAULT_DURATION,
    };
    set((s) => ({ toasts: [...s.toasts, toast] }));
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}));

/**
 * Imperative dispatcher — call from any mutation callback. Mirrors the
 * Sonner ``toast.success / .error`` ergonomics so the surface reads the same
 * as the rest of the app, but routes into the console-scoped store above.
 */
export const consoleToast = {
  success: (title: string, opts?: { description?: string; duration?: number }): string =>
    useConsoleToastStore.getState().push({ tone: "success", title, ...opts }),
  error: (title: string, opts?: { description?: string; duration?: number }): string =>
    useConsoleToastStore.getState().push({ tone: "danger", title, ...opts }),
  info: (title: string, opts?: { description?: string; duration?: number }): string =>
    useConsoleToastStore.getState().push({ tone: "info", title, ...opts }),
};
